import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../../App";
import ReactECharts from "echarts-for-react";
import {
  Calculator, DollarSign, Building, ArrowLeft, TrendingUp, PieChart
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const PayrollTaxPage = () => {
  const [taxSummary, setTaxSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const token = localStorage.getItem("cdace_token");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${API}/admin/payroll/tax-summary`, { headers });
        setTaxSummary(res.data.tax_summary || {});
      } catch (error) {
        console.error("Error:", error);
        if (error.response?.status === 401) navigate("/admin-login");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, navigate]);

  const formatCurrency = (val) => {
    if (!val) return "$0";
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 }).format(val);
  };

  const months = Object.keys(taxSummary).sort().slice(-6);
  const totalPaye = Object.values(taxSummary).reduce((sum, m) => sum + (m.paye || 0), 0);
  const totalUif = Object.values(taxSummary).reduce((sum, m) => sum + (m.uif || 0), 0);
  const totalPension = Object.values(taxSummary).reduce((sum, m) => sum + (m.pension || 0), 0);

  const taxTrendOptions = {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'axis', backgroundColor: 'rgba(15, 23, 42, 0.95)', borderColor: 'rgba(201, 162, 39, 0.3)', textStyle: { color: '#f8fafc' } },
    legend: { data: ['PAYE', 'UIF', 'Pension'], textStyle: { color: '#94a3b8' }, top: 0 },
    grid: { left: '3%', right: '4%', bottom: '3%', top: '15%', containLabel: true },
    xAxis: { type: 'category', data: months, axisLine: { lineStyle: { color: '#334155' } }, axisLabel: { color: '#94a3b8' } },
    yAxis: { type: 'value', axisLine: { show: false }, splitLine: { lineStyle: { color: '#1e293b' } }, axisLabel: { color: '#94a3b8' } },
    series: [
      { name: 'PAYE', type: 'bar', stack: 'tax', data: months.map(m => taxSummary[m]?.paye || 0), itemStyle: { color: '#ef4444' } },
      { name: 'UIF', type: 'bar', stack: 'tax', data: months.map(m => taxSummary[m]?.uif || 0), itemStyle: { color: '#3b82f6' } },
      { name: 'Pension', type: 'bar', stack: 'tax', data: months.map(m => taxSummary[m]?.pension || 0), itemStyle: { color: '#8b5cf6' } }
    ]
  };

  const distributionOptions = {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'item', backgroundColor: 'rgba(15, 23, 42, 0.95)', borderColor: 'rgba(201, 162, 39, 0.3)', textStyle: { color: '#f8fafc' } },
    series: [{
      type: 'pie',
      radius: ['40%', '70%'],
      center: ['50%', '50%'],
      data: [
        { value: totalPaye, name: 'PAYE Tax', itemStyle: { color: '#ef4444' } },
        { value: totalUif, name: 'UIF', itemStyle: { color: '#3b82f6' } },
        { value: totalPension, name: 'Pension', itemStyle: { color: '#8b5cf6' } }
      ],
      label: { color: '#94a3b8' }
    }]
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0b1c2d] flex items-center justify-center">
        <div className="text-[#c9a227] text-xl">Loading Tax Data...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b1c2d] p-6" data-testid="payroll-tax-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link to="/admin" className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
              Tax Deductions
            </h1>
            <p className="text-slate-400 text-sm">PAYE, UIF, and Pension contributions summary</p>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="glass-card">
          <CardContent className="p-4">
            <Calculator className="w-6 h-6 text-red-400 mb-2" />
            <p className="text-2xl font-bold text-red-400">{formatCurrency(totalPaye)}</p>
            <p className="text-xs text-slate-400">Total PAYE</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <Building className="w-6 h-6 text-blue-400 mb-2" />
            <p className="text-2xl font-bold text-blue-400">{formatCurrency(totalUif)}</p>
            <p className="text-xs text-slate-400">Total UIF</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <DollarSign className="w-6 h-6 text-purple-400 mb-2" />
            <p className="text-2xl font-bold text-purple-400">{formatCurrency(totalPension)}</p>
            <p className="text-xs text-slate-400">Total Pension</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <TrendingUp className="w-6 h-6 text-[#c9a227] mb-2" />
            <p className="text-2xl font-bold text-[#c9a227]">{formatCurrency(totalPaye + totalUif + totalPension)}</p>
            <p className="text-xs text-slate-400">Total Deductions</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-slate-200">Tax Trend by Month</CardTitle>
          </CardHeader>
          <CardContent>
            <ReactECharts option={taxTrendOptions} style={{ height: '300px' }} />
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-slate-200">Deduction Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ReactECharts option={distributionOptions} style={{ height: '300px' }} />
          </CardContent>
        </Card>
      </div>

      {/* Monthly Breakdown Table */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="text-slate-200">Monthly Tax Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Month</th>
                  <th>PAYE</th>
                  <th>UIF</th>
                  <th>Pension</th>
                  <th>Total</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {months.map((month) => (
                  <tr key={month}>
                    <td className="text-white font-medium">{month}</td>
                    <td className="text-red-400">{formatCurrency(taxSummary[month]?.paye)}</td>
                    <td className="text-blue-400">{formatCurrency(taxSummary[month]?.uif)}</td>
                    <td className="text-purple-400">{formatCurrency(taxSummary[month]?.pension)}</td>
                    <td className="text-[#c9a227] font-semibold">{formatCurrency(taxSummary[month]?.total)}</td>
                    <td>
                      <Badge className="bg-green-500/20 text-green-400">Submitted</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PayrollTaxPage;
